'use client'

import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, ChevronDown, ListFilter, Play, Pause, Loader2 } from 'lucide-react'
import type { SearchResult } from '@/lib/search'
import { useCoverflow } from '@/lib/use-coverflow'
import Artwork from '@/components/Artwork'

/**
 * Carrousel « coverflow » de l'accueil BLC Music (design validé 2026-09-22).
 *  - Déplaçable (souris / doigt, avec un peu d'élan) + flèches, points, clavier.
 *  - Clic sur une carte latérale → elle vient au centre.
 *  - Clic sur la carte centrale → lecture (ou pause si c'est le titre en cours).
 *  - Onglets : Tout / Artistes du moment / Nouveautés / Nouveaux talents.
 */
export type CoverflowTab = { id: string; label: string; items: SearchResult[]; loading?: boolean; badge: string; badgeClass: string }


/** Miniature YouTube haute définition (la vignette de recherche est souvent petite). */
export function hdThumb(t: { videoId: string; thumbnail?: string }) {
  // « mqdefault » : 16:9 sans bandes noires (hqdefault a des bandes noires).
  if (/^[\w-]{11}$/.test(t.videoId)) return `https://i.ytimg.com/vi/${t.videoId}/mqdefault.jpg`
  return t.thumbnail || ''
}

export function cleanArtist(a: string) {
  return (a || '').replace(/\s*-\s*Topic\s*$/i, '').replace(/\s*VEVO\s*$/i, '').replace(/\s*Official(\s+Music)?(\s+Channel)?\s*$/i, '').trim()
}

const norm = (x: string) => x.toLowerCase().normalize('NFD').replace(/[^a-z0-9]/g, '')

/** Découpe « Artiste - Titre » quand la partie gauche correspond à la chaîne. */
function splitTitle(title: string, artist: string): [string, string] | null {
  const m = /^(.+?)\s+[-–—|:]\s+(.+)$/.exec(title || '')
  if (!m) return null
  const a = norm(cleanArtist(artist)), left = norm(m[1])
  if (!a || !left) return null
  return left.includes(a) || a.includes(left) || left.slice(0, 5) === a.slice(0, 5) ? [m[1].trim(), m[2].trim()] : null
}

/** Nom d'artiste lisible : « Luis Fonsi » plutôt que la chaîne « LuisFonsiVEVO ». */
export function displayArtist(title: string, artist: string) {
  return splitTitle(title, artist)?.[0] || cleanArtist(artist)
}

/** Titre sans le nom de l'artiste ni les mentions « (Clip officiel) » etc. */
export function cleanTitle(title: string, artist: string) {
  const s = splitTitle(title, artist)?.[1] ?? title ?? ''
  return s.replace(/\s*[([](clip|official|officiel|audio|video|vidéo|lyrics?|visuali[sz]er|paroles)[^)\]]*[)\]]/gi, '').trim() || title
}

export default function HomeCoverflow({ tabs, onTab, onPlay, currentId, playing, onToggle }: {
  tabs: CoverflowTab[]
  onTab?: (id: string) => void
  onPlay: (t: SearchResult, list: SearchResult[]) => void
  currentId?: string
  playing: boolean
  onToggle: () => void
}) {
  const [tabId, setTabId] = useState(tabs[0]?.id)
  const [menuOpen, setMenuOpen] = useState(false)
  // Tailles CALCULÉES d'après l'écran : en paysage (téléphone couché, TV) les
  // vignettes deviennent BEAUCOUP plus grandes (≈ 60 % de la hauteur).
  const [dim, setDim] = useState({ c: 220, s: 170 })
  const [portrait, setPortrait] = useState(false)
  useEffect(() => {
    const upd = () => {
      const w = window.innerWidth, h = window.innerHeight
      let c: number
      if (h < 521 && w > h) c = Math.min(Math.round(h * 0.6), 300)            // téléphone en paysage
      else if (h > w || document.documentElement.dataset.tvportrait) {
        // ÉCRAN VERTICAL : grande pochette (≈ 72 % de la largeur)
        const W = document.documentElement.dataset.tvrot && w > h ? h : w
        const H = document.documentElement.dataset.tvrot && w > h ? w : h
        c = Math.round(Math.min(W * 0.72, H * 0.44, 460))
      }
      else c = Math.max(220, Math.min(Math.round(Math.min(w * 0.17, h * 0.3)), 300)) // tablette / ordinateur
      setDim({ c, s: Math.round(c * 0.77) })
      setPortrait(h > w || !!document.documentElement.dataset.tvportrait)
    }
    upd(); window.addEventListener('resize', upd); window.addEventListener('orientationchange', upd)
    return () => { window.removeEventListener('resize', upd); window.removeEventListener('orientationchange', upd) }
  }, [])
  const gap = Math.round(dim.s * 0.93)
  const stageH = dim.c + (portrait ? 52 : 44)
  const tab = tabs.find(t => t.id === tabId) || tabs[0]
  const items = (tab?.items || []).slice(0, 12)
  const n = items.length
  const cf = useCoverflow({
    n, gap: gap + 20, start: 2, resetKey: tabId, activateOnSide: true,
    onActivate: i => { const t = items[i]; if (t) { if (t.videoId === currentId) onToggle(); else onPlay(t, items) } },
  })
  const { active, dragging } = cf
  const center0 = (i: number) => i === active

  return (
    <div className="mb-2">
      {/* CHOIX DE CE QU'ON AFFICHE dans le carrousel (bouton + menu). */}
      <div className="relative flex items-center gap-2 mb-2.5 ml-1">
        <button onClick={() => setMenuOpen(o => !o)} aria-haspopup="listbox" aria-expanded={menuOpen}
          className="inline-flex items-center gap-2 pl-3.5 pr-3 py-1.5 rounded-full text-[12.5px] font-semibold text-white bg-gradient-to-r from-a1 to-a2 shadow-[0_0_14px_rgb(var(--a2)/.45)]">
          <ListFilter size={14} /> {tab?.label} <ChevronDown size={14} className={`transition ${menuOpen ? 'rotate-180' : ''}`} />
        </button>
        <span className="text-[12px] text-white/50 hidden sm:inline">Clique une pochette pour l'écouter</span>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-[140]" onClick={() => setMenuOpen(false)} />
            <div role="listbox" className="absolute left-0 top-full mt-1.5 z-[150] w-60 rounded-2xl bg-spanel border border-white/10 p-1.5 shadow-[0_20px_50px_rgba(0,0,0,.5)]">
              {tabs.map(t => (
                <button key={t.id} role="option" aria-selected={t.id === tab?.id}
                  onClick={() => { setTabId(t.id); onTab?.(t.id); cf.restartAuto(); setMenuOpen(false) }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-[13.5px] text-left transition ${t.id === tab?.id ? 'bg-a2/20 text-white font-semibold' : 'text-white/80 hover:bg-white/[0.06]'}`}>
                  {t.label}
                  <span className="text-[11px] text-white/45 tabular-nums">{t.items.length || ''}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <section
        style={{ height: stageH }}
        className="blc-coverflow relative rounded-[18px] overflow-hidden ring-1 ring-white/[0.06]
                   bg-[linear-gradient(100deg,rgb(var(--adeep))_0%,rgb(var(--a1)/.55)_32%,rgb(var(--adeep))_55%,rgb(var(--a3)/.45)_78%,rgb(var(--adeep))_100%)]"
        onKeyDown={cf.onKeyDown}
        tabIndex={0}
        aria-roledescription="carrousel"
        {...cf.tvProps}
      >
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(500px_200px_at_50%_110%,rgba(255,80,190,.45),transparent_70%),radial-gradient(400px_220px_at_15%_0%,rgba(80,120,255,.35),transparent_70%),radial-gradient(400px_220px_at_88%_10%,rgba(160,70,255,.35),transparent_70%)]" />

        <div
          className={`absolute inset-0 select-none touch-pan-y `}
          style={{ perspective: 1100 }}
          onPointerDown={cf.onPointerDown}
        >
          {tab?.loading && !n && (
            <div className="absolute inset-0 flex items-center justify-center text-white/60"><Loader2 className="animate-spin" /></div>
          )}
          {items.map((t, i) => {
            const o = cf.offset(i), a = Math.abs(o)
            // VERTICAL : toutes les pochettes ont la MÊME taille (seul le transform
            // s'anime → fluide) ; voisines réduites, inclinées, qui dépassent.
            const x = portrait ? o * dim.c * 0.68 : o * gap + Math.sign(o) * Math.min(a, 1) * Math.round(dim.c * 0.2)
            const size = portrait || center0(i) ? dim.c : dim.s
            const s = portrait ? (a < 1 ? 1 - a * 0.22 : 0.78 - Math.min(a - 1, 2) * 0.06) : a < 1 ? 1 : 1 - Math.min(a - 1, 3) * 0.035
            const ry = Math.max(-1, Math.min(1, o)) * (portrait ? -36 : -14)
            const center = i === active
            const isCur = t.videoId === currentId
            const artist = displayArtist(t.title, t.artist)
            return (
              <div key={t.videoId} data-i={i}
                className={`absolute left-1/2 rounded-2xl overflow-hidden shadow-[0_18px_40px_rgba(0,0,0,.55)] will-change-transform
                  ${center ? 'outline outline-2 -outline-offset-2 outline-white/90' : ''}
                  ${dragging ? '' : portrait ? 'transition-[transform,opacity] duration-500 ease-[cubic-bezier(.2,.8,.2,1)]' : 'transition-all duration-500 ease-[cubic-bezier(.2,.8,.2,1)]'}`}
                style={{
                  width: size, height: size, marginLeft: -size / 2, top: 14 + (dim.c - size) / 2,
                  transform: `translateX(${x}px) scale(${s}) rotateY(${ry}deg)`, zIndex: 100 - Math.round(a * 10), opacity: a > (portrait ? 2.3 : 4.2) ? 0 : 1,
                }}
              >
                {/* Vignette CARRÉE pleine : l'image de la vidéo remplit tout, sans bandes noires. */}
                {/* VRAIE pochette (album / single), comme dans le lecteur — jamais l'image de la vidéo. */}
                <div className="absolute inset-0"><Artwork track={t as any} fill strict size={size} rounded="rounded-none" /></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent pointer-events-none" />
                <span className={`absolute top-2 left-2 text-[9.5px] font-bold tracking-wide uppercase px-1.5 py-0.5 rounded-[5px] ${tab.badgeClass}`}>{tab.badge}</span>
                <span className={`absolute left-1/2 top-[38%] -ml-[26px] -mt-[26px] w-[52px] h-[52px] rounded-full grid place-items-center transition-all duration-300
                  ${center ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}
                  ${isCur && playing ? 'bg-a2 text-white shadow-[0_0_24px_rgb(var(--a2)/.7)]' : 'bg-white/90 text-[#111]'}`}>
                  {isCur && playing ? <Pause size={22} className="fill-current" /> : <Play size={22} className="fill-current ml-0.5" />}
                </span>
                <div className="on-media absolute inset-x-2 bottom-3 text-center pointer-events-none [text-shadow:0_2px_10px_rgba(0,0,0,.7)]">
                  <div className="font-black uppercase leading-none truncate" style={{ fontSize: Math.round(size * (center ? 0.08 : 0.1)) }}>{artist}</div>
                  <div className={`truncate mt-1 ${center ? 'text-[15px] font-bold text-a3' : 'text-[11.5px] font-semibold uppercase text-a3'}`}>
                    {cleanTitle(t.title, t.artist)}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <button onClick={cf.prev} aria-label="Précédent"
          className="[@media(pointer:coarse)]:hidden absolute left-2 top-1/2 -mt-[22px] w-11 h-11 z-[120] rounded-full grid place-items-center bg-[#0A0A1E]/55 border-[1.5px] border-white/55 backdrop-blur hover:bg-white/20 transition">
          <ChevronLeft size={20} />
        </button>
        <button onClick={cf.next} aria-label="Suivant"
          className="[@media(pointer:coarse)]:hidden absolute right-2 top-1/2 -mt-[22px] w-11 h-11 z-[120] rounded-full grid place-items-center bg-[#0A0A1E]/55 border-[1.5px] border-white/55 backdrop-blur hover:bg-white/20 transition">
          <ChevronRight size={20} />
        </button>
        <div className="absolute bottom-2 inset-x-0 flex justify-center gap-2 z-[120]">
          {items.map((_, i) => (
            <button key={i} aria-label={`Aller à ${i + 1}`} onClick={() => cf.goToIndex(i)}
              className={`rounded-full transition-all ${i === active ? 'w-2 h-2 bg-white' : 'w-1.5 h-1.5 bg-white/35'}`} />
          ))}
        </div>
      </section>
    </div>
  )
}
